import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FiPlus, FiSearch, FiX, FiZap, FiPhone } from "react-icons/fi";
import { BASE_URL } from "config/api";
import { getDealerPortalPath, Paths } from "router/Paths";

type PublicDealCar = {
  _id: string;
  tag?: string;
  make?: string | { name?: string };
  model?: string;
  year?: string | number;
  price?: string | number;
  dealExpiresAt?: string;
  dealImage?: string;
  images?: string[];
  fuelType?: string;
  location?: string;
};

const formatPrice = (value?: string | number) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0
    ? numeric.toLocaleString("en-US")
    : "";
};

const getMakeName = (car: PublicDealCar) =>
  typeof car.make === "string" ? car.make : car.make?.name || "";

const getCarTitle = (car: PublicDealCar) =>
  [getMakeName(car), car.model, car.year].filter(Boolean).join(" ");

const getDealImage = (car: PublicDealCar) =>
  car.dealImage || car.images?.[0] || "";

const getExpiryText = (value?: string) => {
  if (!value) return "";
  const expiresAt = new Date(value).getTime();
  if (Number.isNaN(expiresAt)) return "";

  const msLeft = expiresAt - Date.now();
  if (msLeft <= 0) return "Expired";

  const minutes = Math.floor(msLeft / (60 * 1000));
  if (minutes < 60) return `${Math.max(1, minutes)} min left`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr left`;

  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} left`;
};

function DealerDealsPage() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [cars, setCars] = useState<PublicDealCar[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(search), 250);
    return () => window.clearTimeout(timeout);
  }, [search]);

  const loadDeals = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${BASE_URL}/deals`, {
        params: {
          search: debouncedSearch,
          limit: 30,
          offset: 0,
        },
      });
      setCars(Array.isArray(response.data?.cars) ? response.data.cars : []);
    } catch {
      setCars([]);
      setError("Could not load deals. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    loadDeals();
  }, [loadDeals]);

  const postLink = useMemo(
    () => getDealerPortalPath(Paths.dealerInventory),
    []
  );

  const openPostForm = () => {
    const dealer = localStorage.getItem("dealer");
    if (!dealer) {
      if (window.confirm("Add your dealer account to post a discount deal?")) {
        window.location.assign(`${getDealerPortalPath(Paths.dealerLogin)}?next=${encodeURIComponent(postLink)}`);
      }
      return;
    }
    window.location.assign(postLink);
  };

  const openCar = (carId: string) => {
    window.location.assign(`https://app.bosscarnetwork.com/share/${carId}`);
  };

  return (
    <main className="min-h-screen bg-[#2C2B34] font-sans text-[#111827]">
      <section className="relative mx-auto min-h-screen w-full max-w-[430px] bg-[#F1F1F1] pb-24">
        <header className="rounded-b-[24px] bg-[#2C2B34] px-5 py-6 text-center text-white">
          <h1 className="text-lg font-semibold">BossCar Deals</h1>
        </header>

        <section className="mx-3 mt-4 rounded-[18px] bg-white p-3">
          <div className="flex min-h-[50px] items-center gap-3 rounded-[14px] border border-gray-200 bg-white px-3">
            <FiSearch className="h-5 w-5 shrink-0 text-gray-500" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search deals"
              className="min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-gray-500"
            />
            {search ? (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500"
                aria-label="Clear search"
              >
                <FiX />
              </button>
            ) : null}
          </div>
        </section>

        <section className="px-3 pt-8">
          {loading ? (
            <div className="rounded-[18px] bg-white p-5">
              <div className="h-44 animate-pulse rounded-2xl bg-gray-100" />
              <div className="mt-4 h-4 w-2/3 animate-pulse rounded bg-gray-100" />
              <div className="mt-3 h-5 w-1/2 animate-pulse rounded bg-gray-100" />
            </div>
          ) : error ? (
            <div className="rounded-[18px] bg-white px-5 py-12 text-center text-sm text-[#EF3E3E]">
              {error}
            </div>
          ) : cars.length ? (
            <div className="flex flex-col gap-4">
              {cars.map((car) => (
                <DealCard key={car._id} car={car} onOpen={openCar} />
              ))}
            </div>
          ) : (
            <div className="rounded-[18px] bg-white px-5 py-12 text-center">
              <FiZap className="mx-auto h-8 w-8 text-[#EF3E3E]" />
              <h2 className="mt-3 text-base font-semibold">
                No live deals right now
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Check again soon for fresh dealer deals.
              </p>
            </div>
          )}
        </section>

        <button
          type="button"
          onClick={() => (window.location.href = "tel:0986868523")}
          className="fixed bottom-6 z-20 flex h-14 w-14 items-center justify-center rounded-full bg-[#22C55E] text-white shadow-lg active:scale-95"
          style={{ left: "max(18px, calc((100vw - 430px) / 2 + 18px))" }}
          aria-label="Call BossCar"
        >
          <FiPhone className="h-7 w-7" />
        </button>

        <button
          type="button"
          onClick={openPostForm}
          className="fixed bottom-6 z-20 flex h-14 w-14 items-center justify-center rounded-full bg-[#EF3E3E] text-white shadow-lg active:scale-95"
          style={{ right: "max(18px, calc((100vw - 430px) / 2 + 18px))" }}
          aria-label="Post discount car"
        >
          <FiPlus className="h-8 w-8" />
        </button>
      </section>
    </main>
  );
}

function DealCard({
  car,
  onOpen,
}: {
  car: PublicDealCar;
  onOpen: (id: string) => void;
}) {
  const image = getDealImage(car);
  const title = getCarTitle(car) || "BossCar deal";
  const price = formatPrice(car.price);
  const expires = getExpiryText(car.dealExpiresAt);

  return (
    <article className="overflow-hidden rounded-[18px] bg-white">
      <button
        type="button"
        onClick={() => onOpen(car._id)}
        className="block w-full text-left"
      >
        <div className="relative aspect-[1.45] w-full bg-gray-100">
          {image ? (
            <img src={image} alt={title} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center text-gray-400">
              No image
            </div>
          )}
          {expires ? (
            <span className="absolute left-3 top-3 rounded-full bg-[#2C2B34]/85 px-3 py-1.5 text-xs font-semibold text-white">
              {expires}
            </span>
          ) : null}
        </div>
        <div className="p-4">
          <h2 className="text-base font-semibold leading-tight">{title}</h2>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {price ? (
              <span className="text-xl font-semibold text-[#EF3E3E]">
                {price} Birr
              </span>
            ) : null}
          </div>
          <div className="mt-3 flex items-center justify-between gap-3">
            <span className="min-w-0 truncate text-xs text-gray-500">
              {[car.fuelType, car.location].filter(Boolean).join(" | ")}
            </span>
            <span className="shrink-0 rounded-full bg-[#EF3E3E] px-3 py-2 text-xs font-semibold text-white">
              View car
            </span>
          </div>
        </div>
      </button>
    </article>
  );
}

export default DealerDealsPage;
